// ============================================================
// Domain — The upsize opportunity being built by the wizard,
// plus its completeness and date-range rules
// ============================================================

import moment from 'moment';
import type { RatsRate } from '../../../services/campaignManager/types';
import { makeBlankTiers, TierRate } from './commissionTier';
import { combineDateAndTime, isEndBeforeStart, LEAD_TIME_DAYS, todayPlus } from './dateTime';

export interface UpsizeOpp {
  id: string;
  /** Ref to the chosen ExposurePackage — the server `key` when the API supplied
   *  one, else the local catalogue `id`. Resolve via `resolveExposurePackage`. */
  package: string | null;
  /** Derived from the selected package's typeOptions at pick-time. Defaults to 'UPSIZE'. */
  opportunityType?: 'UPSIZE' | 'FLASH_SALE';
  startDate: Date | null;
  endDate: Date | null;
  startTime: string;
  endTime: string;
  tiers: TierRate[];
  linkedStandaloneOfferIds: string[];
  campaignId: string | null;
  currency: string;
  networkProof: Array<{ uid: string; name: string; url?: string }>;
  // True once the merchant has actively touched either date field. The
  // wizard prefills `startDate` to today+LEAD_TIME_DAYS as a friendly
  // default (ShopBack review buffer); without this flag the prefill alone
  // would trip the isDirty check and pop the cancel-confirm modal on a
  // pristine Create flow. Flipped true on any DatePicker onChange.
  _dateTouched: boolean;
  /** Full baseline from GET /v2/mission-control/moop/baseline — stored for use at submit time */
  _baseline?: RatsRate | null;
}

export interface WizardData {
  brandId: string;
  opportunities: UpsizeOpp[];
  merchantComment: string;
}

export const makeBlankOpp = (index: number): UpsizeOpp => ({
  id: `opp-${Date.now()}-${index}`,
  package: null,
  startDate: todayPlus(LEAD_TIME_DAYS),
  endDate: null,
  startTime: '00:00',
  endTime: '23:59',
  tiers: makeBlankTiers([]),
  linkedStandaloneOfferIds: [],
  campaignId: null,
  currency: 'SGD',
  networkProof: [],
  _dateTouched: false,
});

export const makeInitialData = (): WizardData => ({
  brandId: '',
  opportunities: [makeBlankOpp(1)],
  merchantComment: '',
});

export const REQUIRED_FIELD_COUNT = 4; // package, startDate, endDate, at least 1 tier rate > baseline

export const countFilledFields = (opp: UpsizeOpp): number => {
  let count = 0;
  if (opp.package) count += 1;
  if (opp.startDate) count += 1;
  if (opp.endDate) count += 1;
  const hasValidRate = opp.tiers.some((t) => t.rate > 0 && t.rate >= t.baseline);
  if (hasValidRate) count += 1;
  return count;
};

export const isOppComplete = (opp: UpsizeOpp): boolean => countFilledFields(opp) === REQUIRED_FIELD_COUNT;

/** True when both dates are set and the end datetime is before the start datetime.
 *  The end picker's disabledDate only guards calendar days, so a same-day end time
 *  earlier than the start time (or a stale end left behind when the start moves later)
 *  slips through without this check. Presence is handled separately by isOppComplete. */
export const isOppDateRangeInvalid = (opp: UpsizeOpp): boolean => {
  if (!opp.startDate || !opp.endDate) return false;
  return isEndBeforeStart(opp.startDate, opp.startTime, opp.endDate, opp.endTime);
};

/** True when the start datetime (date + time) is before the current moment.
 *  The calendar disabledDate guard only operates at day granularity — a start
 *  time of 00:00 on today is still in the past once any part of the day has
 *  elapsed. Mirrors Needle's "Start date should be in the future" check. */
export const isStartInPast = (opp: UpsizeOpp): boolean => {
  if (!opp.startDate) return false;
  return combineDateAndTime(opp.startDate, opp.startTime).isBefore(moment());
};
